import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// utils and helpers
import { searchFilterExpenses } from "../utils/searchFilterExpenses";
import { StatusBadge } from "@/utils/resolveStatusStyle";
import { EXPENSE_STATE, EXPENSE_PAYMENT } from "@/utils/statusStyles";
import { formatDateTime } from "@/utils/formatDateTime";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchExpenses } from "@/services/financeApi";
import { useBusiness } from "@hooks/useBusiness";

export default function useExpensesTable() {
  const { businessId } = useBusiness();

  const {
    data: expenses = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // The POS stores no expenses anywhere in Firestore, so this is empty by
    // design rather than mock data - see services/financeApi.js.
    queryKey: ["finance-expenses", businessId],
    queryFn: () => fetchExpenses(businessId),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("search") || "";
  const stateFilter = searchParams.get("state") || "All";
  const paymentFilter = searchParams.get("payment") || "All";

  const updateParam = useCallback(
    (key, val, emptyValue = "All") => {
      const params = new URLSearchParams(searchParams);
      if (val && val !== emptyValue) params.set(key, val);
      else params.delete(key);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchTerm = useCallback((val) => updateParam("search", val, ""), [updateParam]);
  const updateStateFilter = useCallback((val) => updateParam("state", val), [updateParam]);
  const updatePaymentFilter = useCallback((val) => updateParam("payment", val), [updateParam]);

  const filteredExpenses = useMemo(
    () => searchFilterExpenses(expenses, searchTerm, stateFilter, paymentFilter),
    [expenses, searchTerm, stateFilter, paymentFilter]
  );

  const expenseColumns = useMemo(
    () => [
      {
        accessorKey: "referenceNumber",
        header: "Reference",
        cell: ({ row }) => row.original.referenceNumber || "-",
      },
      {
        accessorKey: "supplier",
        header: "Supplier",
        cell: ({ row }) =>
          row.original.supplier || <span className="text-gray-400 italic">Internal</span>,
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => row.original.category || "-",
      },
      {
        accessorKey: "expenseDate",
        header: "Date",
        cell: ({ row }) => formatDateTime(row.original.expenseDate),
      },
      {
        accessorKey: "totalAmount",
        header: "Amount",
        cell: ({ row }) => (
          <span className="font-semibold text-[#1a1a1a]">
            {formatCurrency(row.original.totalAmount)}
          </span>
        ),
      },
      {
        accessorKey: "state",
        header: "State",
        cell: ({ row }) => <StatusBadge map={EXPENSE_STATE} status={row.original.state} />,
      },
      {
        accessorKey: "paymentStatus",
        header: "Payment",
        cell: ({ row }) => <StatusBadge map={EXPENSE_PAYMENT} status={row.original.paymentStatus} />,
      },
    ],
    []
  );

  const renderExpenseRow = useCallback((row) => {
    return (
      <TableRow key={row.id} className="hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
            {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
  }, []);

  return {
    allExpenses: expenses,
    expenses: filteredExpenses,
    searchTerm,
    updateSearchTerm,
    stateFilter,
    updateStateFilter,
    paymentFilter,
    updatePaymentFilter,
    expenseColumns,
    renderExpenseRow,
    refetch,
    isLoading,
    isError,
  };
}
